import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Box,
  TextField,
  Card,
  TableRow,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  Pagination,
  Tabs,
  Tab,
  MenuItem,
} from '@mui/material';
import api from '../../api';
import { toast } from 'react-toastify';
import CircularProgressWithLabel from '../../utils/loader';

const INITIAL_PAGE_INFO = {
  page: 1,
  total_pages: 0,
  total: 0,
  current_page: 1,
};

const INITIAL_QUERY = {
  status: '',
  employeeName: '',
  consignmentNo: '',
  consigneeName: '',
  dateFilterType: 'deliveryDate',
  from: '',
  to: '',
};

const formatDateTime = (value) => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString();
};

const normalizeListResponse = (data) => {
  const list = data?.deliveries || data?.data || data?.items || [];
  const pageInfo = data?.pageInfo || INITIAL_PAGE_INFO;
  const totalAmount = Number(data?.totalAmount || 0);
  return {
    list: Array.isArray(list) ? list : [],
    pageInfo: { ...INITIAL_PAGE_INFO, ...pageInfo },
    totalAmount,
  };
};

const fetchToPays = async (page, query, isTodayTab) => {
  const endpoint = isTodayTab
    ? '/api/v1/deliveries/today_to_pays'
    : '/api/v1/deliveries/all_to_pays';

  const formattedQuery = {
    status: query.status || '',
    employeeName: query.employeeName || '',
    consignmentNo: query.consignmentNo || '',
    consigneeName: query.consigneeName || '',
    deliveryDateFrom: '',
    deliveryDateTo: '',
    outForDeliveryDateFrom: '',
    outForDeliveryDateTo: '',
  };

  if (query.dateFilterType === 'outForDeliveryDate') {
    formattedQuery.outForDeliveryDateFrom = query.from || '';
    formattedQuery.outForDeliveryDateTo = query.to || '';
  } else {
    formattedQuery.deliveryDateFrom = query.from || '';
    formattedQuery.deliveryDateTo = query.to || '';
  }

  const response = await api.post(endpoint, { query: formattedQuery, page });
  if (response?.data?.success) {
    return normalizeListResponse(response.data);
  }
  throw new Error(response?.data?.message || 'Unable to load to-pay deliveries.');
};

export default function ToPays() {
  const [activeTab, setActiveTab] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [consigneeOptions, setConsigneeOptions] = useState([]);

  const [todayList, setTodayList] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayPageInfo, setTodayPageInfo] = useState(INITIAL_PAGE_INFO);
  const [todayQuery, setTodayQuery] = useState(INITIAL_QUERY);
  const [todayTotalAmount, setTodayTotalAmount] = useState(0);

  const [allList, setAllList] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allPageInfo, setAllPageInfo] = useState(INITIAL_PAGE_INFO);
  const [allQuery, setAllQuery] = useState(INITIAL_QUERY);
  const [allTotalAmount, setAllTotalAmount] = useState(0);

  const isTodayTab = activeTab === 0;
  const loading = isTodayTab ? todayLoading : allLoading;
  const rows = isTodayTab ? todayList : allList;
  const pageInfo = isTodayTab ? todayPageInfo : allPageInfo;
  const totalAmount = isTodayTab ? todayTotalAmount : allTotalAmount;
  const selectedQuery = useMemo(
    () => (isTodayTab ? todayQuery : allQuery),
    [isTodayTab, todayQuery, allQuery]
  );

  const loadData = async (tab, pageToLoad, queryToUse) => {
    const todayTab = tab === 0;
    try {
      if (todayTab) setTodayLoading(true);
      else setAllLoading(true);

      const { list, pageInfo: nextPageInfo, totalAmount: nextTotalAmount } = await fetchToPays(
        pageToLoad,
        queryToUse,
        todayTab
      );

      if (todayTab) {
        setTodayList(list);
        setTodayPageInfo(nextPageInfo);
        setTodayTotalAmount(nextTotalAmount);
      } else {
        setAllList(list);
        setAllPageInfo(nextPageInfo);
        setAllTotalAmount(nextTotalAmount);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to load to-pay deliveries.');
      if (todayTab) {
        setTodayList([]);
        setTodayTotalAmount(0);
      } else {
        setAllList([]);
        setAllTotalAmount(0);
      }
    } finally {
      if (todayTab) setTodayLoading(false);
      else setAllLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [employeeRes, clientRes] = await Promise.all([
        api.get('/api/v1/employees/filter_options'),
        api.get('/api/v1/clients/filter_options'),
      ]);

      if (employeeRes?.data?.success) {
        setEmployeeOptions(
          (employeeRes.data.employees || []).map((emp) => ({
            label: emp?.name || '',
            value: emp?.name || '',
          }))
        );
      }

      if (clientRes?.data?.success) {
        setConsigneeOptions(
          (clientRes.data.clients || []).map((client) => ({
            label: client?.name || '',
            value: client?.name || '',
          }))
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to load filter options.');
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      loadData(0, todayPageInfo.current_page || 1, todayQuery);
      return;
    }
    loadData(1, allPageInfo.current_page || 1, allQuery);
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleQueryChange = (event) => {
    const { name, value } = event.target;
    if (isTodayTab) {
      setTodayQuery((prev) => ({ ...prev, [name]: value }));
      return;
    }
    setAllQuery((prev) => ({ ...prev, [name]: value }));
  };

  const onClickSearch = () => {
    if (isTodayTab) {
      setTodayPageInfo((prev) => ({ ...prev, page: 1, current_page: 1 }));
      loadData(0, 1, todayQuery);
      return;
    }
    setAllPageInfo((prev) => ({ ...prev, page: 1, current_page: 1 }));
    loadData(1, 1, allQuery);
  };

  const onClickReset = () => {
    if (isTodayTab) {
      setTodayQuery(INITIAL_QUERY);
      setTodayPageInfo(INITIAL_PAGE_INFO);
      loadData(0, 1, INITIAL_QUERY);
      return;
    }
    setAllQuery(INITIAL_QUERY);
    setAllPageInfo(INITIAL_PAGE_INFO);
    loadData(1, 1, INITIAL_QUERY);
  };

  const onPageChange = (event, newPage) => {
    if (isTodayTab) {
      setTodayPageInfo((prev) => ({ ...prev, page: newPage, current_page: newPage }));
      loadData(0, newPage, todayQuery);
      return;
    }
    setAllPageInfo((prev) => ({ ...prev, page: newPage, current_page: newPage }));
    loadData(1, newPage, allQuery);
  };

  return (
    <Container maxWidth={false}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" gutterBottom>
          To Pays
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Today To Pays" />
          <Tab label="All To Pays" />
        </Tabs>
      </Card>

      <Card sx={{ mb: 3 }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                lg: 'repeat(5, minmax(150px, 1fr))',
              },
            }}
          >
            <TextField
              select
              label="Employee Name"
              name="employeeName"
              size="small"
              fullWidth
              value={selectedQuery.employeeName}
              onChange={handleQueryChange}
            >
              <MenuItem value="">All</MenuItem>
              {employeeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Consignment Number"
              name="consignmentNo"
              size="small"
              fullWidth
              value={selectedQuery.consignmentNo}
              onChange={handleQueryChange}
            />
            <TextField
              select
              label="Consignee Name"
              name="consigneeName"
              size="small"
              fullWidth
              value={selectedQuery.consigneeName}
              onChange={handleQueryChange}
            >
              <MenuItem value="">All</MenuItem>
              {consigneeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Status" name="status" size="small" fullWidth value={selectedQuery.status} onChange={handleQueryChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="OUT_FOR_DELIVERY">OUT_FOR_DELIVERY</MenuItem>
              <MenuItem value="DELIVERED">DELIVERED</MenuItem>
              <MenuItem value="DELIVERY_FAILED">DELIVERY_FAILED</MenuItem>
              <MenuItem value="RESCHEDULED">RESCHEDULED</MenuItem>
              <MenuItem value="RETURNED">RETURNED</MenuItem>
            </TextField>
            {isTodayTab ? (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={onClickSearch}>Search</Button>
                <Button variant="outlined" onClick={onClickReset}>Reset</Button>
              </Stack>
            ) : (
              <Button variant="contained" onClick={onClickSearch}>Search</Button>
            )}
          </Box>

          {!isTodayTab ? (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  lg: 'repeat(5, minmax(150px, 1fr))',
                },
              }}
            >
              <TextField
                select
                label="Date Filter"
                name="dateFilterType"
                size="small"
                fullWidth
                value={selectedQuery.dateFilterType}
                onChange={handleQueryChange}
              >
                <MenuItem value="deliveryDate">Delivery Date</MenuItem>
                <MenuItem value="outForDeliveryDate">Out Of Delivery Date</MenuItem>
              </TextField>
              <TextField
                label="From"
                name="from"
                type="date"
                size="small"
                fullWidth
                value={selectedQuery.from}
                onChange={handleQueryChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                name="to"
                type="date"
                size="small"
                fullWidth
                value={selectedQuery.to}
                onChange={handleQueryChange}
                InputLabelProps={{ shrink: true }}
              />
              <Box />
              <Button variant="outlined" onClick={onClickReset}>Reset</Button>
            </Box>
          ) : null}
        </Stack>
      </Card>

      <Card>
        {loading ? (
          <CircularProgressWithLabel />
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 1000 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">SNo</TableCell>
                    <TableCell align="center">Consignment No</TableCell>
                    <TableCell align="center">Consignee Name</TableCell>
                    <TableCell align="center">Total Amount</TableCell>
                    <TableCell align="center">Employee Name</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Delivery Date</TableCell>
                    <TableCell align="center">Out Of Delivery Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 &&
                    rows.map((row, index) => (
                      <TableRow hover key={row?.id || `${row?.consignmentNo}-${index}`}>
                        <TableCell align="center">
                          {(parseInt(pageInfo.current_page || 1, 10) - 1) * 10 + index + 1}
                        </TableCell>
                        <TableCell align="center"><b>{row?.consignmentNo || 'NA'}</b></TableCell>
                        <TableCell align="center">{row?.consigneeName || 'NA'}</TableCell>
                        <TableCell align="center">{row?.totalAmount || '0'}</TableCell>
                        <TableCell align="center">{row?.employeeName || 'NA'}</TableCell>
                        <TableCell align="center">{row?.status || 'NA'}</TableCell>
                        <TableCell align="center">{formatDateTime(row?.deliveryDate)}</TableCell>
                        <TableCell align="center">{formatDateTime(row?.outOfDeliveryDate)}</TableCell>
                      </TableRow>
                    ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ height: 200 }}>
                        <Typography variant="h6" color="text.secondary">No to-pay records found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {rows.length > 0 && (
              <Stack alignItems="center" py={2}>
                <Pagination color="primary" onChange={onPageChange} page={pageInfo.current_page || 1} count={pageInfo.total_pages || 1} />
              </Stack>
            )}
          </>
        )}
      </Card>

      <Card sx={{ mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="h6">Total To Pay Amount</Typography>
          <Typography variant="h5">₹{Number(totalAmount || 0).toLocaleString()}</Typography>
        </Stack>
      </Card>
    </Container>
  );
}
